// frontend/src/components/contract/ObligationDashboard.js

import React, { useState, useEffect } from 'react';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Search,
  Filter,
  Download,
  FileText,
  Upload
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import DataTable from '../common/DataTable';
import PageHeader from '../common/PageHeader';
import PageBreadcrumb from '../common/PageBreadcrumb';
import ContractService from '../../services/contractService';
import { useToast } from '../common/Toast';

const ObligationDashboard = () => {
    const { success, error: showError } = useToast();

  // State
  const [loading, setLoading] = useState(true);
  const [allObligations, setAllObligations] = useState([]); // Raw data
  const [filteredObligations, setFilteredObligations] = useState([]); // Display data
  const [contracts, setContracts] = useState([]);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view' or 'edit'
  const [selectedItem, setSelectedItem] = useState(null);
  const [editFormData, setEditFormData] = useState({
    status: '',
    risk: '',
    dueDate: '',
    notes: ''
  });
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Filter States
  const [filters, setFilters] = useState({
    status: 'all',
    risk: 'all',
    contractId: 'all',
    dateRange: 'all' // all, upcoming, overdue
  });

  // --- Data Loading Logic ---

  // Helper to flatten and format data
  // Helper to safely extract contract details
  // Helper to safely extract contract details
  const formatObligation = (record, contractLookup) => {
    // 1. Try to get contract object from record or lookup
    const contractObj = 
      record.contractId && typeof record.contractId === 'object'
        ? record.contractId
        : contractLookup[record.contractId] || {};

    const contractTitle = contractObj.title || 'Untitled Contract';
    
    // 2. Get the Display ID (Human Readable string like "CNT-001")
    const displayId = contractObj.contractId || (typeof record.contractId === 'string' ? record.contractId : '');

    // 3. Get the Database ID (_id like "65c4a...") - CRITICAL FIX
    // If record.contractId is populated (object), use its _id.
    // If looking up by string ID, use the looked-up object's _id.
    const dbId = contractObj._id || contractObj.id;

    const dueDate = record.dueDate ? new Date(record.dueDate) : null;
    const isOverdue = dueDate && dueDate < new Date() && record.complianceStatus !== 'compliant';

    return {
      id: record._id || record.id,
      contractTitle: contractTitle,
      contractId: displayId, // For display in table
      contractDbId: dbId,    // For API calls <--- THIS IS THE FIX
      description: record.obligationDescription,
      type: record.complianceType,
      category: record.complianceCategory,
      status: record.complianceStatus,
      risk: record.riskLevel,
      dueDate: record.dueDate,
      isOverdue: isOverdue,
      regulatoryRef: record.regulatoryReference,
      evidence: record.evidence || [],
      evidenceNotes: record.evidenceNotes
    };
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // 1. Get All Contracts first to build a lookup map
      const contractRes = await ContractService.getContracts({ includeInactive: true });
      const contractList = Array.isArray(contractRes) ? contractRes : contractRes.data || [];
      setContracts(contractList);

      const contractLookup = {};
      contractList.forEach(c => {
        contractLookup[c.contractId || c.id || c._id] = c;
      });

      // 2. Fetch Compliance for ALL contracts
      // Note: Ideally, the backend should have a 'getAllObligations' endpoint. 
      // Since we are working with existing services, we map through contracts.
      const promises = contractList.map(contract => 
        ContractService.getContractCompliance(contract.contractId || contract.id)
          .catch(err => ({ data: [] })) // Catch individual errors so one failure doesn't break all
      );

      const results = await Promise.all(promises);
      
      // 3. Flatten and Format
      const flatList = results
        .map(r => r.data || [])
        .flat()
        .map(record => formatObligation(record, contractLookup));

      setAllObligations(flatList);
      setFilteredObligations(flatList);

    } catch (err) {
      console.error('Failed to load dashboard data', err);
      showError('Failed to load obligations data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // --- Filtering Logic ---

  useEffect(() => {
    let result = [...allObligations];

    // Status Filter
    if (filters.status !== 'all') {
      result = result.filter(o => o.status === filters.status);
    }

    // Risk Filter
    if (filters.risk !== 'all') {
      result = result.filter(o => o.risk === filters.risk);
    }

    // Contract Filter
    if (filters.contractId !== 'all') {
      result = result.filter(o => o.contractId === filters.contractId);
    }

    // Date/Time Filter
    if (filters.dateRange === 'overdue') {
      result = result.filter(o => o.isOverdue);
    } else if (filters.dateRange === 'upcoming') {
      const today = new Date();
      const next30Days = new Date();
      next30Days.setDate(today.getDate() + 30);
      result = result.filter(o => {
        const d = new Date(o.dueDate);
        return d >= today && d <= next30Days && o.status !== 'compliant';
      });
    }

    setFilteredObligations(result);
  }, [filters, allObligations]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setModalMode('view');
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    // Initialize form with current values
    setEditFormData({
      status: item.status || 'pending',
      risk: item.risk || 'medium',
      dueDate: item.dueDate ? new Date(item.dueDate).toISOString().split('T')[0] : '',
      notes: item.evidenceNotes || ''
    });
    setModalMode('edit');
    setEvidenceFile(null); 
    setShowModal(true);
  };

  const handleEditChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (modalMode !== 'edit' || !selectedItem) return;

    setIsSaving(true);
    try {
      // 1. Determine Contract ID (use DB ID, fallback to display ID)
      let targetContractId = selectedItem.contractDbId;
      if (!targetContractId) {
        const matchedContract = contracts.find(c => c.contractId === selectedItem.contractId);
        targetContractId = matchedContract ? (matchedContract._id || matchedContract.id) : null;
      }

      if (!targetContractId) {
        throw new Error("Could not resolve Contract Database ID. Please refresh page.");
      }

      // 2. Update Obligation Data (Text Fields)
      const updatePayload = {
        complianceStatus: editFormData.status,
        riskLevel: editFormData.risk,
        dueDate: editFormData.dueDate,
        evidenceNotes: editFormData.notes
      };

      await ContractService.updateContractCompliance(
        targetContractId,
        selectedItem.id,
        updatePayload
      );

      // 3. Upload Evidence (Only if file is selected)
      if (evidenceFile) {
        const uploadData = new FormData();
        uploadData.append('file', evidenceFile);
        uploadData.append('notes', 'Evidence uploaded via Dashboard'); // Optional: Add a standard note

        await ContractService.uploadComplianceEvidence(
          targetContractId, 
          selectedItem.id, 
          uploadData
        );
      }

      success('Obligation updated successfully');
      setShowModal(false);
      setEvidenceFile(null);
      loadData(); 
    } catch (error) {
      console.error('Update failed', error);
      showError(error.message || 'Failed to update obligation');
    } finally {
      setIsSaving(false);
    }
  };

  // --- Statistics ---
  const stats = {
    total: filteredObligations.length,
    criticalRisk: filteredObligations.filter(o => o.risk === 'high' || o.risk === 'critical').length,
    overdue: filteredObligations.filter(o => o.isOverdue).length,
    compliant: filteredObligations.filter(o => o.status === 'compliant').length
  };

  const columns = [
    {
      key: 'contractTitle',
      label: 'Contract',
      width: '240px',
      render: (value, row) => (
        <div>
          <div className="fw-medium" style={{ fontWeight: '500' }}>{value}</div>
          <div className="text-muted" style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.contractId}
          </div>
        </div>
      ),
    },
    {
      key: 'description',
      label: 'Obligation',
      width: '300px', // Adjusted width for better readability
      render: (text, row) => (
        <div>
          <div className="font-medium text-gray-900">{text}</div>
          {row.regulatoryRef && (
            <div className="text-xs text-gray-500 mt-1">Ref: {row.regulatoryRef}</div>
          )}
        </div>
      )
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      width: '140px',
      render: (value, row) => {
        if (!value) return '—';
        const dateObj = new Date(value);
        return (
          <div
            style={{
              fontSize: '12px',
              color: row.isOverdue ? '#ef4444' : '#374151',
              fontWeight: row.isOverdue ? '500' : '400',
            }}
          >
            {dateObj.toLocaleDateString()}
            {row.isOverdue && <div style={{ fontSize: '10px' }}>Overdue</div>}
          </div>
        );
      },
    },
    {
      key: 'risk',
      label: 'Risk Level',
      width: '110px',
      render: (risk) => {
        const riskConfig = {
          high: 'badge-danger bg-red-100 text-red-800',
          medium: 'badge-warning bg-yellow-100 text-yellow-800',
          low: 'badge-success bg-green-100 text-green-800',
        };
        const label = risk ? risk.charAt(0).toUpperCase() + risk.slice(1) : 'Medium';
        // Using standard Bootstrap/Tailwind hybrid classes for compatibility
        const className = riskConfig[risk] || 'bg-gray-100 text-gray-800';
        
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${className}`}>
            {label}
          </span>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: '140px',
      render: (status) => {
        const statusConfig = {
          compliant: {
            className: 'bg-green-100 text-green-800',
            icon: <CheckCircle size={14} />,
            label: 'Compliant'
          },
          'non-compliant': {
            className: 'bg-red-100 text-red-800',
            icon: <XCircle size={14} />,
            label: 'Non-Compliant'
          },
          pending: { 
            className: 'bg-yellow-100 text-yellow-800', 
            icon: <Clock size={14} />,
            label: 'Pending'
          },
        };
        const config = statusConfig[status] || statusConfig.pending;

        return (
          <span className={`d-flex align-items-center gap-1 px-2 py-1 rounded-md w-fit ${config.className}`}>
            {config.icon}
            <span style={{fontSize: '12px'}}>{config.label}</span>
          </span>
        );
      },
    }
  ];

  const headerActions = [
    {
      icon: <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />,
      onClick: loadData,
      variant: 'outline'
    },
    {
      label: 'Export Report',
      icon: <Download size={18} />,
      onClick: () => console.log('Export logic here'), // Placeholder for export
      variant: 'outline'
    }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Obligation Dashboard" 
        description="Centralized view of all contract obligations, risks, and compliance status across the organization."
        actions={headerActions}
      />

      <div className="p-6 space-y-6">
        
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <KPICard 
            title="Total Obligations" 
            value={stats.total} 
            icon={Shield} 
            color="blue" 
          />
          <KPICard 
            title="High Risk Items" 
            value={stats.criticalRisk} 
            icon={AlertTriangle} 
            color="red" 
            subText="Requires immediate attention"
          />
          <KPICard 
            title="Overdue" 
            value={stats.overdue} 
            icon={Clock} 
            color="orange" 
            subText="Past due date"
          />
          <KPICard 
            title="Compliance Rate" 
            value={`${stats.total ? Math.round((stats.compliant / stats.total) * 100) : 0}%`} 
            icon={CheckCircle} 
            color="green" 
          />
        </div>

        {/* Filters & Data Table */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          {/* Filter Bar */}
          <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2 text-gray-600">
              <Filter size={18} />
              <span className="font-medium">Filters:</span>
            </div>
            
            <select 
              className="form-select text-sm border-gray-300 rounded-md"
              value={filters.contractId}
              onChange={(e) => handleFilterChange('contractId', e.target.value)}
            >
              <option value="all">All Contracts</option>
              {contracts.map(c => (
                <option key={c.id || c.contractId} value={c.id || c.contractId}>
                  {c.title}
                </option>
              ))}
            </select>

            <select 
              className="form-select text-sm border-gray-300 rounded-md"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="compliant">Compliant</option>
              <option value="non-compliant">Non-Compliant</option>
              <option value="pending">Pending</option>
            </select>

            <select 
              className="form-select text-sm border-gray-300 rounded-md"
              value={filters.risk}
              onChange={(e) => handleFilterChange('risk', e.target.value)}
            >
              <option value="all">All Risks</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            <select 
              className="form-select text-sm border-gray-300 rounded-md"
              value={filters.dateRange}
              onChange={(e) => handleFilterChange('dateRange', e.target.value)}
            >
              <option value="all">All Dates</option>
              <option value="overdue">Overdue</option>
              <option value="upcoming">Upcoming (30 Days)</option>
            </select>

            {/* Reset Filter Button */}
            {(filters.status !== 'all' || filters.risk !== 'all' || filters.contractId !== 'all' || filters.dateRange !== 'all') && (
              <button 
                onClick={() => setFilters({ status: 'all', risk: 'all', contractId: 'all', dateRange: 'all' })}
                className="text-sm text-blue-600 hover:text-blue-800 underline ml-auto"
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Table with Actions */}
          {loading ? (
             <div className="flex flex-col items-center justify-center h-64">
               <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
               <p className="text-gray-500">Aggregating compliance data...</p>
             </div>
          ) : (
            <DataTable 
              data={filteredObligations}
              columns={columns}
              searchable={true}
              pagination={true}
              pageSize={10}
              searchPlaceholder="Search obligations..."
              emptyMessage="No obligations found matching your filters."
              // ADDED ACTION PROPS
              showActions={true}
              onView={handleView}
              onEdit={handleEdit}
            />
          )}
        </div>
      </div>

      {/* --- View/Edit Modal (Unified Style) --- */}
      {showModal && selectedItem && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              width: '100%',
              maxWidth: '720px',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '24px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
          >
            {/* Header Section */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
                borderBottom: '1px solid #eee',
                paddingBottom: '16px'
              }}
            >
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#111827' }}>
                {modalMode === 'edit' ? 'Edit Obligation' : 'Obligation Details'}
              </h3>
              
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowModal(false)}
                style={{ border: 'none', background: 'transparent', color: '#6b7280', cursor: 'pointer' }}
              >
                <XCircle size={24} />
              </button>
            </div>

            <form onSubmit={handleModalSubmit}>
              
              {/* --- VIEW MODE UI --- */}
              {modalMode === 'view' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Contract</label>
                      <div className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded border border-gray-100">
                        {selectedItem.contractTitle}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Obligation</label>
                      <div className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded border border-gray-100">
                        {selectedItem.description}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Due Date</label>
                      <div className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded border border-gray-100">
                        {selectedItem.dueDate ? new Date(selectedItem.dueDate).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <div className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded border border-gray-100 capitalize">
                        {selectedItem.status}
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-4">
                    {/* Notes Display (Fallback for older text-based evidence) */}
                    {selectedItem.evidenceNotes && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Evidence / Notes</label>
                        <div className="text-sm text-gray-800 bg-gray-50 p-3 rounded-md border border-gray-200 whitespace-pre-wrap">
                          {selectedItem.evidenceNotes}
                        </div>
                      </div>
                    )}

                    {/* NEW: Evidence History Section with File Names */}
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Evidence History</h4>
                    
                    {selectedItem.evidence && selectedItem.evidence.length > 0 ? (
                      <div className="space-y-3">
                        {selectedItem.evidence.map((file, index) => (
                          <div key={index} className="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-100">
                            {/* File Icon */}
                            <FileText className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                            
                            <div className="flex-1">
                              {/* File Name Display */}
                              <p className="text-sm font-medium text-gray-900 break-all">
                                {file.fileName || "Unknown File"}
                              </p>
                              
                              {/* Metadata: Date & User */}
                              <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                                <span>{new Date(file.uploadDate).toLocaleDateString()}</span>
                                <span>•</span>
                                <span>Attested by: {file.attestedBy?.firstName || 'User'}</span>
                              </div>

                              {/* Optional Notes for this specific file */}
                              {file.notes && (
                                <p className="text-xs text-gray-600 mt-1 italic">"{file.notes}"</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No evidence uploaded yet.</p>
                    )}
                  </div>
                </>
              )}

              {/* --- EDIT MODE UI --- */}
              {modalMode === 'edit' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Read-Only Fields */}
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Contract</label>
                      <div className="mt-1 text-sm text-gray-500 bg-gray-50 p-2 rounded border border-gray-100">
                        {selectedItem.contractTitle}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Obligation Description</label>
                      <div className="mt-1 text-sm text-gray-500 bg-gray-50 p-2 rounded border border-gray-100">
                        {selectedItem.description}
                      </div>
                    </div>

                    {/* Editable Fields */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select 
                        className="form-select w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        value={editFormData.status}
                        onChange={(e) => handleEditChange('status', e.target.value)}
                      >
                        <option value="pending">Pending</option>
                        <option value="compliant">Compliant</option>
                        <option value="non-compliant">Non-Compliant</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Risk Level</label>
                      <select 
                        className="form-select w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        value={editFormData.risk}
                        onChange={(e) => handleEditChange('risk', e.target.value)}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                      <input 
                        type="date"
                        className="form-control w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        value={editFormData.dueDate}
                        onChange={(e) => handleEditChange('dueDate', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Evidence / Notes</label>
                    <textarea 
                      rows="3"
                      className="form-control w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      value={editFormData.notes}
                      onChange={(e) => handleEditChange('notes', e.target.value)}
                    />
                  </div>

                  <div className="border-t border-gray-100 pt-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Attach New Evidence (Optional)</h4>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md bg-gray-50">
                      <div className="space-y-1 text-center">
                        <Upload className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="flex text-sm text-gray-600 justify-center">
                          <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500 px-2">
                            <span>Upload a file</span>
                            <input 
                              id="file-upload" 
                              name="file-upload" 
                              type="file" 
                              className="sr-only" 
                              onChange={(e) => setEvidenceFile(e.target.files[0])}
                            />
                          </label>
                        </div>
                        <p className="text-xs text-gray-500">PDF, PNG, JPG up to 10MB</p>
                      </div>
                    </div>
                    {evidenceFile && (
                      <div className="mt-2 p-2 bg-blue-50 text-blue-700 text-sm rounded flex items-center border border-blue-100">
                        <FileText size={16} className="mr-2" />
                        Selected: {evidenceFile.name}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Footer Actions */}
              <div 
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginTop: '24px',
                  paddingTop: '16px',
                  borderTop: '1px solid #eee'
                }}
              >
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 mr-3"
                  onClick={() => setShowModal(false)}
                >
                  {modalMode === 'view' ? 'Close' : 'Cancel'}
                </button>
                
                {modalMode === 'edit' && (
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

// Simple Internal KPI Card Component
const KPICard = ({ title, value, icon: Icon, color, subText }) => {
  const colorClasses = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-l-blue-600' },
    red: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-l-red-600' },
    green: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-l-green-600' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-l-orange-600' }
  };
  
  const current = colorClasses[color] || colorClasses.blue;

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-4 shadow-sm border-l-4 ${current.border}`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="text-gray-500 text-sm font-medium">{title}</p>
          <h3 className="text-2xl font-bold text-gray-800 mt-1">{value}</h3>
          {subText && <p className="text-xs text-gray-400 mt-1">{subText}</p>}
        </div>
        <div className={`p-2 rounded-full ${current.bg}`}>
          <Icon size={20} className={current.text} />
        </div>
      </div>
    </div>
  );
};

export default ObligationDashboard;